import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, Toast } from '../../../core/services/toast.service';
import { I18nService } from '../../../core/i18n/i18n.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[380px] z-[100] flex flex-col-reverse gap-3 pointer-events-none">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          class="toast-card pointer-events-auto flex items-center gap-3.5 pl-4 pr-3 py-3.5 rounded-2xl text-sm"
          [ngClass]="getToastClasses()"
        >
          <!-- Accent bar -->
          <div class="absolute left-0 top-3 bottom-3 w-[3px] rounded-full" [ngClass]="getBarClass(toast)"></div>

          <!-- Icon circle -->
          <div class="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" [ngClass]="getIconBgClass(toast)">
            @switch (toast.type) {
              @case ('success') {
                <svg class="w-[18px] h-[18px] text-brand-jade" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              }
              @case ('error') {
                <svg class="w-[18px] h-[18px] text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
              @case ('warning') {
                <svg class="w-[18px] h-[18px] text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM2.697 16.126c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
                </svg>
              }
            }
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-[13px] leading-none mb-0.5" [ngClass]="getTitleClass(toast)">
              {{ getTitle(toast) }}
            </p>
            <p class="text-[13px] text-gray-500 leading-snug break-words">{{ toast.message }}</p>
          </div>

          <!-- Close button -->
          <button
            (click)="toastService.dismiss(toast.id)"
            class="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
          >
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Progress bar -->
          <div class="absolute bottom-0 left-4 right-4 h-[2px] rounded-full overflow-hidden bg-gray-100">
            <div class="h-full rounded-full toast-progress" [ngClass]="getProgressClass(toast)" [style.animation-duration]="getDuration(toast)"></div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-card {
      position: relative;
      overflow: hidden;
      background: white;
      box-shadow:
        0 4px 6px -1px rgb(0 0 0 / 0.05),
        0 10px 15px -3px rgb(0 0 0 / 0.08),
        0 20px 25px -5px rgb(0 0 0 / 0.04);
      animation: toast-slide-in 0.35s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
    }

    @keyframes toast-slide-in {
      from {
        opacity: 0;
        transform: translateX(40px) scale(0.96);
      }
      to {
        opacity: 1;
        transform: translateX(0) scale(1);
      }
    }

    @keyframes toast-progress {
      from { width: 100%; }
      to   { width: 0%; }
    }

    .toast-progress {
      animation: toast-progress linear forwards;
    }
  `],
})
export class ToastComponent {
  readonly toastService = inject(ToastService);
  readonly i18n = inject(I18nService);

  getToastClasses(): string {
    return 'border border-gray-100';
  }

  getBarClass(toast: Toast): string {
    switch (toast.type) {
      case 'success': return 'bg-brand-jade';
      case 'error':   return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
    }
  }

  getIconBgClass(toast: Toast): string {
    switch (toast.type) {
      case 'success': return 'bg-brand-jade-50';
      case 'error':   return 'bg-red-50';
      case 'warning': return 'bg-amber-50';
    }
  }

  getTitleClass(toast: Toast): string {
    switch (toast.type) {
      case 'success': return 'text-brand-jade';
      case 'error':   return 'text-red-700';
      case 'warning': return 'text-amber-700';
    }
  }

  getTitle(toast: Toast): string {
    switch (toast.type) {
      case 'success': return this.i18n.t('common.toastTitles.success');
      case 'error':   return this.i18n.t('common.toastTitles.error');
      case 'warning': return this.i18n.t('common.toastTitles.warning');
    }
  }

  getProgressClass(toast: Toast): string {
    switch (toast.type) {
      case 'success': return 'bg-brand-jade';
      case 'error':   return 'bg-red-400';
      case 'warning': return 'bg-amber-400';
    }
  }

  getDuration(toast: Toast): string {
    switch (toast.type) {
      case 'success': return '4s';
      case 'error':   return '5s';
      case 'warning': return '4.5s';
    }
  }
}
